import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Col, Form as AntdForm, Row } from 'antd';
import { CollapseBlock } from '../../../../common/index.jsx';
import { RadioButtonsPicker } from '../../../../common/form/FormElements.jsx';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Form from '../../../../antd/form/Form/Form.jsx';
import FormTextField from '../../../../antd/form/FormTextField/FormTextField.jsx';
import FormSelect from '../../../../antd/form/FormSelect/FormSelect.jsx';
import FormSwitch from '../../../../antd/form/FormSwitch/FormSwitch.jsx';
import FormButtonsContainer from '../../../../antd/form/FormButtonsContainer/FormButtonsContainer.jsx';
import FormButton from '../../../../antd/form/FormButton/FormButton.jsx';
import { url } from '../../../../antd/form/FormValidation.js';
import { MENU_ITEM_TYPES } from '../../../../../utils/constants/enums.js';

const MenuForm = ({
  menuItemDetail,
  itemId,
  onSave,
  contentMessage,
  users,
  articlesCollection,
}) => {
  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.auth.user);
  const [linkType, setLinkType] = useState(MENU_ITEM_TYPES.ARTICLE.id); // default to 'article'

  const [form] = AntdForm.useForm();
  const switchSkValue = AntdForm.useWatch('sk', form);
  const switchEnValue = AntdForm.useWatch('en', form);

  useEffect(() => {
    let linkType = MENU_ITEM_TYPES.ARTICLE.id; // default for 'article'
    if (menuItemDetail) {
      linkType = menuItemDetail.item_type; // use item_type directly
    }
    setLinkType(linkType);
  }, [menuItemDetail]);

  const articlesOptions = useMemo(() => {
    return (
      articlesCollection &&
      articlesCollection.map((article) => ({
        label: article.title_sk,
        value: article.id,
      }))
    );
  }, [articlesCollection]);

  const isCreate = (!itemId && true) || false;
  const initials = {
    text_sk: null,
    text_en: null,
    destination_en: null,
    destination: null,
    article_id: null,
    responsible_person_id: (currentUser && (currentUser.person_id ?? currentUser.id)) || null,
    sk: true,
    en: false,
    order: 1, // default for API
    item_type: linkType,
    editors: [],
    ...menuItemDetail,
  };

  const beforeSave = (values) => {
    let tmp = { ...values, item_type: linkType };

    if (linkType === MENU_ITEM_TYPES.ARTICLE.id) {
      tmp = {
        ...tmp,
        text_sk: null,
        text_en: null,
        destination: null,
        destination_en: null,
      };
    }
    if (linkType === MENU_ITEM_TYPES.SUBMENU.id) {
      tmp = { ...tmp, destination: null, article_id: null, destination_en: null };
    }
    if (
      linkType === MENU_ITEM_TYPES.EXTERNAL.id ||
      linkType === MENU_ITEM_TYPES.INTERNAL.id
    ) {
      tmp = { ...tmp, article_id: null };
    }
    if (linkType === MENU_ITEM_TYPES.EXTERNAL.id) {
      tmp = { ...tmp, destination_en: null };
    }

    isCreate ? onSave(tmp) : onSave(tmp, itemId);
  };

  const internalSlugValidator = (_, value) => {
    if (linkType !== MENU_ITEM_TYPES.INTERNAL.id || !value) {
      return Promise.resolve();
    }

    const isValidSlug = /^[a-z0-9]+(?:[\/-][a-z0-9]+)*$/.test(value);

    return isValidSlug
      ? Promise.resolve()
      : Promise.reject(
          new Error(
            t('URL adresa môže obsahovať iba malé písmená, čísla, spojovník a lomku.'),
          ),
        );
  };

  const atLeastOneSwitchValidator = (_, value) => {
    const sk = form.getFieldValue('sk');
    const en = form.getFieldValue('en');
    if ((value ?? false) || sk || en) {
      return Promise.resolve();
    } else {
      return Promise.reject(
        new Error(t('Aspoň jedna verzia menu musí byť vybratá.')),
      );
    }
  };

  const clearFieldErrors = (fieldNames) => {
    form.setFields(fieldNames.map((name) => ({ name, errors: [] })));
  };

  const handleLanguageSwitchChange = (language, checked) => {
    const languageFields =
      language === 'sk' ? ['text_sk', 'destination'] : ['text_en', 'destination_en'];
    const fieldsToValidate = checked ? ['sk', 'en', ...languageFields] : ['sk', 'en'];

    form.validateFields(fieldsToValidate);

    if (!checked) {
      clearFieldErrors(languageFields);
    }
  };

  return (
    <Form
      form={form}
      onFinish={(values) => {
        beforeSave(values);
      }}
      initialValues={initials}
      layout="vertical"
      style={{ margin: '10px' }}
    >
      <CollapseBlock title={t('Typ položky menu')} collapsible={true}>
        {contentMessage && <p>{contentMessage}</p>}

        <RadioButtonsPicker
          items={[
            {
              id: MENU_ITEM_TYPES.ARTICLE.id,
              label: t(MENU_ITEM_TYPES.ARTICLE.label),
            },
            {
              id: MENU_ITEM_TYPES.SUBMENU.id,
              label: t(MENU_ITEM_TYPES.SUBMENU.label),
            },
            {
              id: MENU_ITEM_TYPES.EXTERNAL.id,
              label: t(MENU_ITEM_TYPES.EXTERNAL.label),
            },
            {
              id: MENU_ITEM_TYPES.INTERNAL.id,
              label: t(MENU_ITEM_TYPES.INTERNAL.label),
            },
          ]}
          value={linkType}
          onChange={(chosenId) => {
            setLinkType(chosenId);
            form.setFieldsValue({
              item_type: chosenId, // Update item_type directly
              article_id:
                chosenId === MENU_ITEM_TYPES.ARTICLE.id
                  ? null
                  : form.getFieldValue('article_id'),
              destination:
                chosenId === MENU_ITEM_TYPES.ARTICLE.id
                  ? null
                  : form.getFieldValue('destination'),
              destination_en:
                chosenId === MENU_ITEM_TYPES.INTERNAL.id
                  ? form.getFieldValue('destination_en')
                  : null,
            });
          }}
        />
      </CollapseBlock>
      <CollapseBlock
        collapsible={true}
        title={t('Nastavenia pre vybraný typ položky menu')}
      >
        <Row>
          <Col span={4}>
            <FormSwitch
              label={t('Zobraziť v slovenskom menu')}
              name={'sk'}
              rules={[{ validator: atLeastOneSwitchValidator }]}
              onChange={(checked) => {
                handleLanguageSwitchChange('sk', checked);
              }}
            />
          </Col>
          <Col>
            <FormSwitch
              label={t('Zobraziť v anglickom menu')}
              name={'en'}
              rules={[{ validator: atLeastOneSwitchValidator }]}
              onChange={(checked) => {
                handleLanguageSwitchChange('en', checked);
              }}
            />
          </Col>
        </Row>
        {linkType !== MENU_ITEM_TYPES.ARTICLE.id && (
          <>
            <FormTextField
              label={t('Názov (slovensky)')}
              name={'text_sk'}
              disabled={linkType === MENU_ITEM_TYPES.ARTICLE.id}
              rules={[
                {
                  required:
                    switchSkValue && linkType !== MENU_ITEM_TYPES.ARTICLE.id,
                  message: t(
                    'Názov (slovensky) je povinný, ak je položka zobrazená v slovenskom menu.',
                  ),
                },
              ]}
            />
            <FormTextField
              label={t('Názov (anglicky)')}
              name="text_en"
              disabled={linkType === MENU_ITEM_TYPES.ARTICLE.id}
              rules={[
                {
                  required:
                    switchEnValue && linkType !== MENU_ITEM_TYPES.ARTICLE.id,
                  message: t(
                    'Názov (anglicky) je povinný, ak je položka zobrazená v anglickom menu.',
                  ),
                },
              ]}
            />
          </>
        )}

        {linkType === MENU_ITEM_TYPES.ARTICLE.id ? (
          <FormSelect
            label={t('Článok')}
            name={'article_id'}
            options={articlesOptions}
            rules={[{ required: true, message: t('Pole je povinné') }]}
          />
        ) : linkType !== MENU_ITEM_TYPES.SUBMENU.id ? (
          <>
            <FormTextField
              label={t('URL adresa (SK)')}
              name={'destination'}
              rules={[
                {
                  required:
                    linkType === MENU_ITEM_TYPES.EXTERNAL.id ||
                    (linkType === MENU_ITEM_TYPES.INTERNAL.id && switchSkValue),
                  message:
                    linkType === MENU_ITEM_TYPES.INTERNAL.id
                      ? t(
                          'Slovenská URL je povinná, ak je položka zobrazená v slovenskom menu.',
                        )
                      : t('Pole je povinné'),
                },
                linkType === MENU_ITEM_TYPES.EXTERNAL.id && url(t), //kontrola URL len pre external
                /*{ validator: internalSlugValidator },*/
              ]}
            />
            {linkType === MENU_ITEM_TYPES.INTERNAL.id && (
              <FormTextField
                label={t('URL adresa (EN)')}
                name={'destination_en'}
                rules={[
                  {
                    required: switchEnValue,
                    message: t(
                      'Anglická URL je povinná, ak je položka zobrazená v anglickom menu.',
                    ),
                  },
                  /*{ validator: internalSlugValidator },*/
                ]}
              />
            )}
          </>
        ) : null}

        {linkType === MENU_ITEM_TYPES.SUBMENU.id ||
        linkType === MENU_ITEM_TYPES.ARTICLE.id ? (
          <FormSelect
            label={t('Editori')}
            name={'editors'}
            options={users}
            mode={'multiple'}
          />
        ) : null}
        <FormSelect
          label={t('Zodpovedná osoba')}
          name={'responsible_person_id'}
          options={users}
          rules={[{ required: true }]}
        />
        <FormButtonsContainer>
          <FormButton submitOnClick={true} submitKey="menu">
            {t('Uložiť')}
          </FormButton>
        </FormButtonsContainer>
      </CollapseBlock>
    </Form>
  );
};

MenuForm.propTypes = {
  menuItemDetail: PropTypes.object,
  onSave: PropTypes.func,
  itemId: PropTypes.string,
  parentId: PropTypes.number,
  contentMessage: PropTypes.string,
  isEnglish: PropTypes.bool,
  users: PropTypes.array,
  articlesCollection: PropTypes.array,
};

export default MenuForm;
